/**
 * Theme preference: auto (follows the OS), light, or dark.
 *
 * Persisted in `localStorage` under `af_theme`, the same idiom as
 * `af_device_id` (operations.ts:116): try the DOM storage, fall back
 * silently when it is unusable (private mode, no DOM). Default is auto.
 * The resolved surface (`light` | `dark`) is applied as `data-theme` on
 * `html`; see NotebookContainer.
 */

export type Theme = 'auto' | 'light' | 'dark';

const THEME_KEY = 'af_theme';
const DARK = '(prefers-color-scheme: dark)';

export function readTheme(): Theme {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    if (stored === 'auto' || stored === 'light' || stored === 'dark') return stored;
  } catch {
    // localStorage unusable — default below.
  }
  return 'auto';
}

export function writeTheme(value: Theme): void {
  try {
    localStorage.setItem(THEME_KEY, value);
  } catch {
    // best effort
  }
}

/** Resolve `auto` against the OS preference; `light`/`dark` pass through. */
export function resolveTheme(pref: Theme): 'light' | 'dark' {
  if (pref !== 'auto') return pref;
  return typeof matchMedia !== 'undefined' && matchMedia(DARK).matches ? 'dark' : 'light';
}
